import { useState } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Paginate from '../components/Paginate';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Benchmark from '../components/Benchmark';
import BackToTop from '../components/BackToTop';
import { toast } from 'react-toastify';

import { useGetBenchmarksQuery, useBulkDeleteByVersionMutation } from '../slices/benchmarksApiSlice';
import { useListScope, SCOPE_ALL } from '../hooks/useListScope';

// scope='all'  → All Benchmarks (admins also get Contributor filter + bulk delete)
// scope='mine' → My Benchmarks (the signed-in user's claimed benchmarks)
export default function BenchmarksListScreen({ scope = SCOPE_ALL }) {
  const listScope = useListScope(scope);
  const { mine } = listScope;

  // Persist list preferences separately per scope
  const storageKey = (name) => `${mine ? 'myBenchmarks' : 'benchmarks'}${name}`;

  const [sortBy, setSortBy] = useState(
    localStorage.getItem(storageKey('SortBy')) || 'startTime_desc'
  );
  const [profileFilter, setProfileFilter] = useState(
    localStorage.getItem(storageKey('ProfileFilter')) || 'All'
  );
  const [clientVersionFilter, setClientVersionFilter] = useState(
    localStorage.getItem(storageKey('ClientVersionFilter')) || 'All'
  );
  const [contributorFilter, setContributorFilter] = useState(
    localStorage.getItem(storageKey('ContributorFilter')) || 'All'
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    Number(localStorage.getItem(storageKey('PerPage'))) || 10
  );
  // Unchecked by default: runs with modified profile settings are hidden
  const [includeModified, setIncludeModified] = useState(
    localStorage.getItem(storageKey('IncludeModified')) === 'true'
  );

  const { pageNumber } = useParams();
  const page = Number(pageNumber) || 1;

  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const isLoggedIn = Boolean(userInfo?._id);
  const safeUserId = isLoggedIn ? userInfo._id : undefined;

  // Contributor filter and bulk delete are admin tools for the public list;
  // Client Version is admin-only there but always useful on your own list
  const isAdminList = Boolean(userInfo?.isAdmin) && !mine;
  const showClientVersion = isAdminList || mine;

  // Speed sorts always exclude modified runs (enforced server-side); the saved
  // checkbox preference is kept and applies again on other sorts
  const isSpeedSort = sortBy.startsWith('write_') || sortBy.startsWith('read_');

  const { data, isLoading, error } = useGetBenchmarksQuery({
    mine,
    userId: safeUserId,
    page,
    pageSize: itemsPerPage,
    profile: profileFilter,
    clientVersion: clientVersionFilter,
    contributor: contributorFilter,
    sortBy,
    includeModified,
  });

  const [bulkDeleteByVersion, { isLoading: isBulkDeleting }] = useBulkDeleteByVersionMutation();

  const handleBulkDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete ALL benchmarks from client version v${clientVersionFilter}? This CANNOT be undone.`
      )
    ) {
      try {
        const result = await bulkDeleteByVersion({ clientVersion: clientVersionFilter }).unwrap();
        toast.success(result.message);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Message variant='danger'>
        {error?.data?.message || error.error}
      </Message>
    );
  }

  const benchmarks = data.benchmarks;

  // Profile, version, and contributor lists come from the server (unfiltered, for the dropdowns)
  const allProfiles = ['All', ...(data.allProfiles || [])];
  const allClientVersions = ['All', ...(data.allClientVersions || [])];
  // Contributors are keyed by user id (names are not unique); name is display-only
  const allContributors = data.allContributors || [];
  // Excluding modified runs (default, and always on speed sorts) is a filter too
  const filtersActive = profileFilter !== 'All' || clientVersionFilter !== 'All' ||
    (isAdminList && contributorFilter !== 'All') || !includeModified || isSpeedSort;
  // Claim guidance only when the user owns nothing at all, not when filters hide everything
  const noneClaimed = mine && data.totalOwned === 0;

  // Helper: extract write/read bandwidth from operations array
  // const getWriteSpeed = (b) =>
  //   b.operations?.find(op => op.modeDisplay?.toUpperCase().includes("WRITE"))?.bandwidth ?? 0;
  // const getReadSpeed = (b) =>
  //   b.operations?.find(op => op.modeDisplay?.toUpperCase().includes("READ"))?.bandwidth ?? 0;

  // Server handles all sorting — no client-side sort needed
  const displayBenchmarks = benchmarks;

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    localStorage.setItem(storageKey('SortBy'), value);
    // Sort is server-side and affects all pages — reset to page 1
    if (page !== 1) navigate(basePath);
  };

  const handleProfileChange = (e) => {
    const value = e.target.value;
    setProfileFilter(value);
    localStorage.setItem(storageKey('ProfileFilter'), value);
    // Filter is server-side — must reset to page 1 so totalPages recalculates
    if (page !== 1) navigate(basePath);
  };

  const handleClientVersionChange = (e) => {
    const value = e.target.value;
    setClientVersionFilter(value);
    localStorage.setItem(storageKey('ClientVersionFilter'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleContributorChange = (e) => {
    const value = e.target.value;
    setContributorFilter(value);
    localStorage.setItem(storageKey('ContributorFilter'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleItemsPerPageChange = (e) => {
    const value = Number(e.target.value);
    setItemsPerPage(value);
    localStorage.setItem(storageKey('PerPage'), value);
    // Page size changes totalPages — must reset to page 1
    if (page !== 1) navigate(basePath);
  };

  const handleIncludeModifiedChange = (e) => {
    const value = e.target.checked;
    setIncludeModified(value);
    localStorage.setItem(storageKey('IncludeModified'), String(value));
    if (page !== 1) navigate(basePath);
  };

  // basePath used to reset to page 1 when filter/pageSize changes
  const basePath = listScope.benchmarksPath;

  return (
    <>
      <h1 style={{ marginTop: '32px' }}>
        {mine ? 'My Benchmarks' : 'All Benchmarks'}{typeof data?.totalBenchmarks === 'number' ? ` (${data.totalBenchmarks})` : ''}
      </h1>

      <div className='d-flex flex-wrap gap-4 mb-4'>
        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Sort Order:</span>
          <select value={sortBy} onChange={handleSortChange} className='form-select form-select--custom w-auto'>
            <optgroup label="Start Time">
              <option className='option--custom' value='startTime_desc'>Newest first</option>
              <option className='option--custom' value='startTime_asc'>Oldest first</option>
            </optgroup>
            <optgroup label="Write Speed">
              <option className='option--custom' value='write_desc'>Write (Highest first)</option>
              <option className='option--custom' value='write_asc'>Write (Lowest first)</option>
            </optgroup>
            <optgroup label="Read Speed">
              <option className='option--custom' value='read_desc'>Read (Highest first)</option>
              <option className='option--custom' value='read_asc'>Read (Lowest first)</option>
            </optgroup>
            <optgroup label="Model Name">
              <option className='option--custom' value='model_asc'>Model Name (A → Z)</option>
              <option className='option--custom' value='model_desc'>Model Name (Z → A)</option>
            </optgroup>
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Type (Profile):</span>
          <select value={profileFilter} onChange={handleProfileChange} className='form-select form-select--custom w-auto'>
            {allProfiles.map(p => (
              <option className='option--custom' key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {showClientVersion && (
          <div className='d-flex align-items-center'>
            <span className='me-2 fw-bold'>Client Version:</span>
            <select value={clientVersionFilter} onChange={handleClientVersionChange} className='form-select form-select--custom w-auto'>
              {allClientVersions.map(v => (
                <option className='option--custom' key={v} value={v}>{v === 'All' ? 'All' : `v${v}`}</option>
              ))}
            </select>
          </div>
        )}

        {isAdminList && (
          <div className='d-flex align-items-center'>
            <span className='me-2 fw-bold'>Contributor:</span>
            <select value={contributorFilter} onChange={handleContributorChange} className='form-select form-select--custom w-auto'>
              <option className='option--custom' value='All'>All</option>
              <option className='option--custom' value='Anonymous'>Anonymous</option>
              {allContributors.map(c => (
                <option className='option--custom' key={c.contributorId} value={c.contributorId}>{c.displayName}</option>
              ))}
            </select>
          </div>
        )}

        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Records per Page:</span>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange} className='form-select form-select--custom w-auto'>
            <option className='option--custom' value="5">5</option>
            <option className='option--custom' value="10">10</option>
            <option className='option--custom' value="15">15</option>
            <option className='option--custom' value="20">20</option>
            <option className='option--custom' value="25">25</option>
            <option className='option--custom' value="50">50</option>
          </select>
        </div>

        <div
          className='d-flex align-items-center'
          title={isSpeedSort
            ? 'Speed rankings only include unmodified runs'
            : 'Include benchmarks run with modified profile settings'}
        >
          <Form.Check
            type='checkbox'
            id='include-modified-checkbox'
            className='fw-bold mb-0'
            label='Include modified'
            checked={includeModified && !isSpeedSort}
            disabled={isSpeedSort}
            onChange={handleIncludeModifiedChange}
          />
          {isSpeedSort && (
            <span className='ms-2 small text-muted fst-italic'>
              (speed rankings use unmodified runs only)
            </span>
          )}
        </div>
      </div>

      {displayBenchmarks.length === 0 && (
        <Message variant='info'>
          {noneClaimed ? (
            <>
              No benchmarks claimed yet. Go to{' '}
              <Link to='/me/systems'>My Systems</Link>{' '}
              to claim a system and its benchmarks.
            </>
          ) : filtersActive ? (
            'No benchmarks match the selected filters.'
          ) : (
            'No benchmarks found.'
          )}
        </Message>
      )}

      <Row>
        {displayBenchmarks.map((benchmark) => (
          <Col key={benchmark._id} sm={12} md={12} lg={12} xl={12}>
            <Benchmark benchmark={benchmark} to={listScope.benchmarkPath(benchmark._id)} />
          </Col>
        ))}
      </Row>

      <div className='d-flex justify-content-center' style={{ margin: '32px 0' }}>
        <Paginate
          totalPages={data?.totalPages}
          currentPage={data?.currentPage}
          basePath={basePath}
          firstPageIsBasePath={true}
        />
      </div>
      <BackToTop heroThreshold={200} />

      {isAdminList && clientVersionFilter !== 'All' && (
        <Card className='border-danger mt-3 shadow-sm mb-5'>
          <Card.Header className='bg-danger text-white fw-bold'>
            Admin Danger Zone
          </Card.Header>
          <Card.Body className='text-center p-4'>
            <p className='mb-4'>
              Warning: This will permanently delete all benchmarks
              from client version <strong>v{clientVersionFilter}</strong>. This CANNOT be undone.
            </p>
            <Button
              variant='danger'
              size='lg'
              className='w-100 fw-bold py-3'
              onClick={handleBulkDelete}
              disabled={isBulkDeleting || !data?.totalBenchmarks}
            >
              {isBulkDeleting ? 'DELETING…' : `DELETE ALL v${clientVersionFilter} BENCHMARKS`}
            </Button>
          </Card.Body>
        </Card>
      )}
    </>
  );
}